import { Container, Sprite, Texture } from "pixi.js";

export type NumberLayerOptions = {
  tilePx: number; // world tile size at scale=1
  getValueAt: (x: number, y: number, idx: number) => number;
  toLabel?: (value: number, x: number, y: number, idx: number) => string;
  shouldDraw?: (value: number, x: number, y: number, idx: number) => boolean;

  fontFamily?: string;
  fontWeight?: string;
  fillStyle?: string;
  strokeStyle?: string;
  lineWidth?: number;

  supersample?: number; // default 2
  insetPx?: number; // default 0
  maxTextureSize?: number; // default 1024 (safer for iOS/WKWebView)
  drawGrid?: boolean; // default true (subtle grid)
};

/**
 * Create a tiled numbers layer as a single Container of chunked Sprites.
 * Safe on iOS: each chunk canvas/texture is clamped to maxTextureSize.
 */
export async function createNumbersLayer(
  gridW: number,
  gridH: number,
  opts: NumberLayerOptions
): Promise<Container> {
  const {
    tilePx,
    getValueAt,
    toLabel = (v) => String(v),
    shouldDraw = (v) => v !== 0,

    fontFamily = "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    fontWeight = "700",
    fillStyle = "rgba(0,0,0,0.9)",
    strokeStyle = "#ffffff",
    lineWidth = 2,

    supersample = 1,
    insetPx = 0,
    maxTextureSize = 1024,
    drawGrid = true,
  } = opts;

  if (gridW <= 0 || gridH <= 0 || tilePx <= 0) {
    throw new Error("createNumbersLayerTiled: invalid grid or tile size");
  }

  // Pixel size of a single tile at render-time (supersampled)
  const t = tilePx * supersample;

  // How many tiles can we fit per chunk side within maxTextureSize?
  const maxTilesPerChunkX = Math.max(1, Math.floor(maxTextureSize / t));
  const maxTilesPerChunkY = Math.max(1, Math.floor(maxTextureSize / t));

  // Container holding all chunk sprites
  const container = new Container();
  container.eventMode = "none";

  // Precompute font
  const fontPx = Math.max(1, Math.floor(t * 0.6 - insetPx * 2));
  const half = t * 0.5;

  // Iterate chunks
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) throw new Error("2D canvas context not available");

  for (let y0 = 0; y0 < gridH; y0 += maxTilesPerChunkY) {
    const tilesHigh = Math.min(maxTilesPerChunkY, gridH - y0);

    for (let x0 = 0; x0 < gridW; x0 += maxTilesPerChunkX) {
      const tilesWide = Math.min(maxTilesPerChunkX, gridW - x0);

      // Canvas size for this chunk (in render pixels)
      const canvasW = Math.max(1, Math.floor(tilesWide * t));
      const canvasH = Math.max(1, Math.floor(tilesHigh * t));

      canvas.width = canvasW;
      canvas.height = canvasH;

      // Optional: white backing for legibility
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.fillRect(0, 0, canvasW, canvasH);

      // Text setup
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${fontWeight} ${fontPx}px ${fontFamily}`;

      // Draw numbers for cells in this chunk
      for (let dy = 0; dy < tilesHigh; dy++) {
        const y = y0 + dy;
        const rowBase = y * gridW;
        for (let dx = 0; dx < tilesWide; dx++) {
          const x = x0 + dx;
          const idx = rowBase + x;
          const value = getValueAt(x, y, idx);
          if (!shouldDraw(value, x, y, idx)) continue;

          const label = toLabel(value, x, y, idx);
          const cx = Math.floor(dx * t + half);
          const cy = Math.floor(dy * t + half);

          if (lineWidth > 0) {
            ctx.strokeStyle = strokeStyle;
            ctx.lineWidth = lineWidth;
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.strokeText(label, cx, cy);
          }
          ctx.fillStyle = fillStyle;
          ctx.fillText(label, cx, cy);
        }
      }

      // Subtle grid inside the chunk (optional)
      if (drawGrid) {
        ctx.strokeStyle = "rgba(0,0,0,0.2)";
        ctx.lineWidth = 1;
        // Vertical grid lines
        for (let dx = 0; dx <= tilesWide; dx++) {
          const xpos = Math.round(dx * t);
          ctx.beginPath();
          ctx.moveTo(xpos, 0);
          ctx.lineTo(xpos, canvasH);
          ctx.stroke();
        }
        // Horizontal grid lines
        for (let dy = 0; dy <= tilesHigh; dy++) {
          const ypos = Math.round(dy * t);
          ctx.beginPath();
          ctx.moveTo(0, ypos);
          ctx.lineTo(canvasW, ypos);
          ctx.stroke();
        }
      }

      // === Upload safely via ImageBitmap ===
      const bitmap = await createImageBitmap(canvas);
      const tex = Texture.from(bitmap);
      tex.source.scaleMode = "linear"; // smooth when zooming in
      // tex.source.mipmap = "on";
      tex.source.autoGenerateMipmaps = true;

      // Free bitmap soon (after GL upload; microtask is usually enough)
      // queueMicrotask(() => bitmap.close?.());

      const sprite = new Sprite(tex);
      sprite.eventMode = "none";

      // Place this chunk in world coordinates.
      // We rendered at `supersample` pixels per world unit.
      sprite.scale.set(1 / supersample, 1 / supersample);
      sprite.position.set(x0 * tilePx, y0 * tilePx);

      container.addChild(sprite);
    }
  }

  return container;
}

/**
 * Create a tinted highlight layer that mirrors a tiled numbers layer.
 *
 * @param numbersBase  The container returned by createNumbersLayerTiled.
 * @param tintColor    Tint color to apply (default: gray).
 */
export function createNumbersHighlight(
  numbersBase: Container,
  tintColor = 0x888888
): Container {
  const highlight = new Container();
  highlight.eventMode = "none";

  for (const child of numbersBase.children) {
    if (child instanceof Sprite) {
      const clone = new Sprite(child.texture);

      // mirror transform
      clone.position.copyFrom(child.position);
      clone.scale.copyFrom(child.scale);
      clone.rotation = child.rotation;

      // tinting
      clone.tint = tintColor;
      clone.alpha = 1;

      clone.eventMode = "none";

      highlight.addChild(clone);
    }
  }

  return highlight;
}

/**
 * Create (or update) a 1px-per-tile alpha mask where tiles equal to
 * `selectedValue` are opaque (alpha=255) and others are transparent (alpha=0).
 *
 * The sprite is scaled by `tilePx` so each mask pixel maps to one world tile.
 */
export function createOrUpdateSelectionMask(
  existing: Sprite | undefined,
  gridW: number,
  gridH: number,
  tilePx: number,
  numbers: Uint32Array,
  selectedValue: number
): Sprite {
  // 1) Tiny canvas: gridW x gridH
  const canvas = document.createElement("canvas");
  canvas.width = gridW;
  canvas.height = gridH;

  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) throw new Error("2D canvas context unavailable");

  const img = ctx.createImageData(gridW, gridH);
  const data = img.data; // RGBA

  // Write alpha channel: 255 when value matches, else 0
  const total = gridW * gridH;
  for (let i = 0; i < total; i++) {
    const on = numbers[i] === selectedValue;
    const o = i * 4;
    // RGB can be anything; alpha drives the mask
    data[o + 0] = 255;
    data[o + 1] = 255;
    data[o + 2] = 255;
    data[o + 3] = on ? 255 : 0;
  }
  ctx.putImageData(img, 0, 0);

  // 2) Upload to a texture and force nearest sampling (v8 API)
  const tex: Texture = Texture.from(canvas);
  // In Pixi v8, sampling options live on the TextureSource:
  tex.source.scaleMode = "nearest"; // <- CRISP UPSCALING
  // tex.source.mipmap = "off"; // <- avoid mip filtering when zoomed out

  // 3) Install/replace on a sprite
  if (existing) {
    const old = existing.texture;
    existing.texture = tex;
    existing.scale.set(tilePx, tilePx);
    // Important: ensure integer scale for crisp mapping
    existing.roundPixels = true; // helps when the parent has fractional transforms
    old.destroy(true);
    return existing;
  }

  const spr = new Sprite(tex);
  spr.eventMode = "none";
  spr.scale.set(tilePx, tilePx); // 1px -> 1 tile
  spr.roundPixels = true; // snap to device pixels when rendered
  // spr.position.set(mosaicX, mosaicY); // only if your group isn't already positioned
  return spr;
}
